import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Trash2, Info, Bot, User,
  Loader, Shield, Eye, MessageSquare
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import useFirmInfo from '../../hooks/useFirmInfo';
import { AI_ASSISTANT_ENABLED } from '../../config/features';

const PortalAI = () => {
  const navigate = useNavigate();

  // Route-level gate: a disabled feature must not be reachable by direct URL.
  useEffect(() => {
    if (!AI_ASSISTANT_ENABLED) navigate('/portal/dashboard', { replace: true });
  }, [navigate]);
  const { firm_phone: firmPhone } = useFirmInfo();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [contextData, setContextData] = useState(null);

  useEffect(() => {
    // Add welcome message
    setMessages([
      {
        role: 'assistant',
        content: `Hello! I'm Astraea, your AI assistant. I can help you with questions about your cases, invoices, and documents.

**What I can help with:**
- Status updates on your cases
- Information about your invoices and payments
- Document-related questions
- General legal process questions

**Note:** I can only access information related to your specific account. I cannot provide legal advice - for legal questions, please contact your attorney directly.

How can I help you today?`
      }
    ]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await clientPortalService.chatWithAI(userMessage);
      const isAttorneyRequest = /speak.*(attorney|lawyer)|talk.*attorney|contact.*attorney|call.*attorney|need.*attorney|reach.*attorney/i.test(userMessage);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.response,
        showContactAction: isAttorneyRequest
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClear = async () => {
    try {
      await clientPortalService.clearAIConversation();
      setMessages([{
        role: 'assistant',
        content: 'Conversation cleared. How can I help you?'
      }]);
    } catch (err) {
      console.error('Failed to clear conversation:', err);
    }
  };

  const loadContextPreview = async () => {
    try {
      const data = await clientPortalService.getAIContextPreview();
      setContextData(data);
      setShowContext(true);
    } catch (err) {
      console.error('Failed to load context:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex flex-col">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="text-virgo-teal-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center space-x-3">
              <img src="/astraea-avatar.png" alt="Astraea" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <h1 className="text-xl font-bold text-white">Astraea AI</h1>
                <p className="text-xs text-virgo-teal-400">Your case assistant</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={loadContextPreview}
              className="p-2 text-virgo-teal-400 hover:text-cyan-400 transition-colors"
              title="What can AI see?"
            >
              <Eye size={20} />
            </button>
            <button
              onClick={handleClear}
              className="p-2 text-virgo-teal-400 hover:text-red-400 transition-colors"
              title="Clear conversation"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Security Notice */}
      <div className="max-w-4xl mx-auto w-full px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-center space-x-2 text-xs text-virgo-teal-500">
          <Shield size={14} />
          <span>AI only has access to your case information - not internal firm discussions</span>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                    : message.error
                    ? 'bg-red-500/20 border border-red-500/30 text-red-300'
                    : 'bg-virgo-navy-700 border border-virgo-teal-700/30 text-virgo-teal-100'
                }`}
              >
                <div className="flex items-start space-x-2">
                  {message.role === 'assistant' && (
                    <img src="/astraea-avatar.png" alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  )}
                  <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap">
                    {message.content}
                  </div>
                </div>
                {message.showContactAction && (
                  <div className="mt-3 pt-3 border-t border-virgo-teal-700/30 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate('/portal/messages')}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all"
                    >
                      <MessageSquare size={16} />
                      <span>Send a Message to the Firm</span>
                    </button>
                    {firmPhone && (
                      <a
                        href={`tel:${firmPhone.replace(/[^0-9+]/g, '')}`}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 text-virgo-teal-300 text-sm rounded-lg hover:border-cyan-500/50 transition-colors"
                      >
                        <span>Call the firm</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-virgo-navy-700 border border-virgo-teal-700/30 rounded-2xl px-4 py-3">
                <div className="flex items-center space-x-2">
                  <img src="/astraea-avatar.png" alt="" className="w-5 h-5 rounded-full object-cover" />
                  <Loader className="animate-spin text-cyan-400" size={18} />
                  <span className="text-virgo-teal-400 text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-virgo-teal-700/30 bg-virgo-navy-800/80 backdrop-blur-sm p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your cases, invoices, or documents..."
                rows={1}
                className="w-full px-4 py-3 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-xl text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                style={{ minHeight: '48px', maxHeight: '150px' }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="p-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
            >
              <Send size={20} />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              'What is my case status?',
              'Do I have any outstanding invoices?',
              'When is my next court date?',
              'How do I upload a document?',
              'Can you explain my invoice?',
              'What happens next in my case?',
              'I need to speak with my attorney'
            ].map((suggestion, index) => (
              <button
                key={index}
                onClick={() => {
                  setInput(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-3 py-1 text-xs bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-full text-virgo-teal-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
          <div className="text-center mt-2">
            <span className="text-xs text-virgo-teal-600">Powered by Astraea AI</span>
          </div>
        </div>
      </div>

      {/* Context Preview Modal */}
      {showContext && contextData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Info className="text-cyan-400" size={20} />
                <h2 className="text-lg font-semibold text-white">What AI Can See</h2>
              </div>
              <button
                onClick={() => setShowContext(false)}
                className="text-virgo-teal-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-sm text-cyan-300">
                <Shield size={16} className="inline mr-2" />
                {contextData.note}
              </div>

              {contextData.cases?.length > 0 && (
                <div>
                  <h3 className="font-medium text-white mb-2">Your Cases ({contextData.cases.length})</h3>
                  <div className="space-y-2">
                    {contextData.cases.map((c, i) => (
                      <div key={i} className="bg-virgo-navy-800/50 rounded-lg p-3 text-sm">
                        <p className="text-white font-medium">{c.case_name}</p>
                        <p className="text-virgo-teal-400">Status: {c.status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contextData.invoices?.length > 0 && (
                <div>
                  <h3 className="font-medium text-white mb-2">Your Invoices ({contextData.invoices.length})</h3>
                  <div className="space-y-2">
                    {contextData.invoices.slice(0, 5).map((inv, i) => (
                      <div key={i} className="bg-virgo-navy-800/50 rounded-lg p-3 text-sm">
                        <p className="text-white">{inv.invoice_number}</p>
                        <p className="text-virgo-teal-400">${inv.total_amount} - {inv.status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalAI;
